import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

export const DoNotSellPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    state: '',
    requestType: 'do-not-sell',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate submission - in production, this would go to your backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Clear any marketing/tracking preferences
    const preferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('parler_cloud_cookie_preferences', JSON.stringify(preferences));
    window['ga-disable-G-41FMTVX9LS'] = true;
    
    setLoading(false);
    setSubmitted(true);
    toast.success('Your request has been submitted successfully');
  };

  return (
    <div className="bg-[var(--bg-primary)] min-h-screen pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 px-4 py-2 mb-6 text-sm text-[#2a5ff6] rounded-full">
            <Shield size={16} />
            Privacy Rights
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Do Not Sell or Share My Personal Information</h1>
          <p className="text-white/60">Exercise your privacy rights under CCPA/CPRA</p>
        </div>

        {/* Content */}
        <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-2xl p-6 md:p-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={32} className="text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Request Submitted</h2>
              <p className="text-white/70 mb-6">
                We have received your request and will process it within 45 days as required by law. 
                You will receive a confirmation email at the address you provided.
              </p>
              <Link to="/" className="btn-primary inline-flex">
                Return to Home
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">Your Privacy Rights</h2>
                <p className="text-white/70 mb-4">
                  Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), 
                  California residents have the right to:
                </p>
                <ul className="space-y-2 text-white/70">
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#2a5ff6] mt-1 flex-shrink-0" />
                    <span>Opt-out of the sale or sharing of their personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#2a5ff6] mt-1 flex-shrink-0" />
                    <span>Request deletion of their personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#2a5ff6] mt-1 flex-shrink-0" />
                    <span>Know what personal information is collected and how it's used</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#2a5ff6] mt-1 flex-shrink-0" />
                    <span>Request correction of inaccurate personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#2a5ff6] mt-1 flex-shrink-0" />
                    <span>Limit use and disclosure of sensitive personal information</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-white/10 pt-8">
                <h3 className="text-lg font-semibold text-white mb-4">Submit Your Request</h3>
                <p className="text-white/60 text-sm mb-6">
                  Complete the form below to exercise your privacy rights. We will verify your identity 
                  and respond within 45 days.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#2a5ff6]"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#2a5ff6]"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">State of Residence *</label>
                    <select
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#2a5ff6]"
                    >
                      <option value="">Select your state</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="VA">Virginia</option>
                      <option value="UT">Utah</option>
                      <option value="other">Other US State</option>
                      <option value="international">Outside US</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Request Type *</label>
                    <select
                      required
                      value={formData.requestType}
                      onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#2a5ff6]"
                    >
                      <option value="do-not-sell">Do Not Sell or Share My Personal Information</option>
                      <option value="delete">Delete My Personal Information</option>
                      <option value="access">Access My Personal Information</option>
                      <option value="correct">Correct My Personal Information</option>
                      <option value="limit">Limit Use of Sensitive Personal Information</option>
                    </select>
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#2a5ff6] text-white py-4 rounded-xl font-semibold hover:bg-[#4d7ff8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="mt-8 p-4 bg-white/5 rounded-xl">
                <p className="text-xs text-white/50">
                  <strong className="text-white/70">Note:</strong> We do not discriminate against consumers who exercise 
                  their privacy rights. Processing your request will not affect the quality of services you receive. 
                  For more information, please review our{' '}
                  <Link to="/privacy" className="text-[#2a5ff6] hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoNotSellPage;
